import React, { useState, useEffect, useCallback, useMemo } from "react";

// routing
import { useNavigate, useParams } from "react-router-dom";
import AddCircleRoundedIcon from "@mui/icons-material/AddCircleRounded";

// components
import DataTable from "../../../../components/Tables/DataTable";
import ContainerCard from "../../../../components/Cards/ContainerCard";
import { CircularProgress } from "@mui/material";

// hooks
import useLazyQueryAuth from "../../../../hooks/useLazyQueryAuth";
import useQueryAuth from "../../../../hooks/useQueryAuth";
import useAuth from "../../../../hooks/useAuth";
import useMutationAuth from "../../../../hooks/useMutationAuth";

// graphql
import { GET_USER_TABLE_PREFERENCES } from "../../../../graphql/userTablePreferences";
import {
	GET_ALL_INVOICES_LIST,
	UNDO_INVOICE,
} from "../../../../graphql/invoices";

// other imports
import { Parse } from "../../../../JSON.editor";
import Mnemonic from "../../../../Mnemonics.json";
import urls from "../../../../urls";
import UTC2Local from "../../../../UTC2Local";
import componentDisplay from "../../../../componentDisplay";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { PrintIcon, UndoIcon } from "../../../../components/Icon";
import colors from "../../../../assets/theme/light/colors";
import { decodeUnicodeString } from "../../../../utils/decodeUnicodeString";
import { removeNullProps } from "../../../../utils/removeNullProps";

const InvoicesList = ({ fromFavorites = false }) => {
	const { pID, pName } = useParams();
	const navigate = useNavigate();
	const { storeUserGuid, userRoles } = useAuth();
	const { manageParameters } = userRoles;
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();

	const [re, setRe] = useState(false);
	const [data, setData] = useState(null);
	const [defaultFilters, setDefaultFilters] = useState({});
	const [columns, setColumns] = useState([]);
	const [order, setOrder] = useState("");
	const [orderBy, setOrderBy] = useState("");
	const [selected, setSelected] = useState([]);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(20);
	const [search, setSearch] = useState("");

	const {
		loading: getColumnsLoading,
		tokenChange: columnsWait,
		refetch,
	} = useQueryAuth(
		GET_USER_TABLE_PREFERENCES,
		"GetUserTablePreferences",
		{
			variables: {
				model: {
					table: Mnemonic.Tables["Invoices"],
					userID: storeUserGuid,
				},
			},
		},
		{
			onCompleted: (response) => {
				const col = response?.GetUserTablePreferences?.columns || null;
				if (col) {
					const parsed = JSON.parse(
						decodeUnicodeString(col),
						(key, value) => Parse(key, value, UTC2Local, componentDisplay)
					);

					if (parsed.columns) {
						setColumns(
							parsed.columns.map((column) => {
								if (column.id === "Type") {
									return {
										...column,
										renderCell: (row) => {
											const typeValue = row?.type;
											return typeValue === "INVOICE"
												? "Invoice"
												: typeValue === "CN"
													? "Credit Memo"
													: typeValue;
										},
									};
								}
								return removeNullProps(column);
							})
						);
					}
					if (parsed.defaultSort) setOrderBy(parsed.defaultSort);
					if (parsed.defaultSortDirection)
						setOrder(parsed.defaultSortDirection);
				}
			},
		}
	);

	const { mfunction: getAllInvoices, loading } = useLazyQueryAuth(
		GET_ALL_INVOICES_LIST,
		"GetProjectInvoicesList"
	);

	const fetchTableData = useCallback(
		async (
			page = 0,
			pageSize = 0,
			order = "",
			orderBy = "",
			search = "",
			filters = null
		) => {
			try {
				await getAllInvoices(
					{
						variables: {
							model: {
								keys: { page: page + 1, pageSize },
								orderColumn: orderBy,
								orderDirection: order,
								keyword: search,
								projectGuid: pID,
							},
						},
					},
					(response) => {
						const result =
							response?.GetProjectInvoicesList
								?.projectInvoicesListResponse;
						if (result) {
							setData({
								GetProjectInvoicesList: {
									projectInvoicesListResponse: result,
								},
							});
						}
					}
				);
			} catch (error) {
				console.error("Fetch table data error:", error);
			}
		},
		[getAllInvoices, pID]
	);

	useEffect(() => {
		if (!columnsWait) {
			fetchTableData(
				page,
				rowsPerPage,
				order,
				orderBy,
				search,
				defaultFilters
			);
		}
	}, [columnsWait, re, defaultFilters]);

	const projectInvoiceData =
		data?.GetProjectInvoicesList?.projectInvoicesListResponse;
	const rows = projectInvoiceData?.projectInvoices || [];
	const numberOfRecords = projectInvoiceData?.totalNumberOfRecords || 0;

	const { mfunction: undoRecord, loading: undoLoading } = useMutationAuth(
		UNDO_INVOICE,
		"UndoInvoicesAllocation"
	);

	const handleUndo = async () => {
		try {
			await undoRecord({ variables: { projectGuid: pID } });
			setRe(!re);
			enqueueSnackbar(t("invoices.list.undo.success"), {
				variant: "success",
			});
		} catch (err) {
			console.error("Undo failed", err);
			enqueueSnackbar(t("invoices.list.undo.error"), { variant: "error" });
		}
	};

	const {
		favorite_printable_invoices,
		printable_invoices,
		favorite_appliedInvoices,
		appliedInvoices,
	} = urls;

	const customButtons = [
		{
			label: "project.list.print",
			handleClick: () => {
				const target = fromFavorites
					? favorite_printable_invoices
					: printable_invoices;
				navigate(
					target.replace(
						"/:pName/:pID",
						`/${encodeURIComponent(pName)}/${encodeURIComponent(pID)}`
					)
				);
			},
			condition: true,
			icon: true,
			iconButton: <PrintIcon color="#000000" />,
		},
		{
			label: "invoices.btn.undo",
			handleClick: handleUndo,
			condition: !selected || selected.length === 0,
			disabled: undoLoading || loading,
			icon: true,
			iconButton: (
				<UndoIcon
					sx={{
						color: "var(--color--light-logo-blue)",
						fontSize: "23px !important",
						border: "1px solid var(--color--light-logo-blue)",
						padding: "2px",
						borderRadius: "50%",
					}}
				/>
			),
		},
	];

	const pagination = {
		rows,
		columns,
		order,
		setOrder,
		orderBy,
		setOrderBy,
		selected,
		defaultFilters,
		setDefaultFilters,
		setSelected,
		search,
		setSearch,
		setPage,
		setRowsPerPage,
		numberRecords: numberOfRecords,
		page,
		rowsPerPage,
		fetchTableData,
		loading: loading || columnsWait || undoLoading,
		searchText: "invoice.list.search.label",
		refetch,
		removeFilters: true,
		tableMnemonic: "invoices",
		customButtons,
		readOnly: manageParameters !== "M",
		disableRowClick: (row) => row?.type === "CN",
		generateRoute: (row) => {
			if (row?.type === "CN") return "";
			const base = fromFavorites
				? favorite_appliedInvoices
				: appliedInvoices;
			return base
				.replace(
					"/:pName/:pID",
					`/${encodeURIComponent(pName)}/${encodeURIComponent(pID)}`
				)
				.replace(
					"/:iName/:iID",
					`/${encodeURIComponent(row?.documentNo)}/${encodeURIComponent(row?.projectInvoiceGuid)}`
				);
		},
	};

	return (
		<div>
			{getColumnsLoading ? (
				<ContainerCard
					sx={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						padding: "20px",
					}}
				>
					<CircularProgress />
				</ContainerCard>
			) : (
				<DataTable {...pagination} identifier="projectInvoiceGuid" />
			)}
		</div>
	);
};

export default InvoicesList;
